import qs from 'qs'
import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { UseQueryParamsParams } from './interfaces/useQueryParams.interface'

export const useQueryParams = <T>({
  qsStringifyOptions,
}: UseQueryParamsParams = {}): {
  queryParams: T
  removeQueryParams: (queryParams: (keyof T)[]) => void
  setQueryParams: (queryParams: T) => void
  updateQueryParams: (queryParams: T) => void
} => {
  const location = useLocation()

  const navigate = useNavigate()

  const queryParams = useMemo<T>(
    () =>
      qs.parse(location.search, { ignoreQueryPrefix: true }) as unknown as T,
    [location.search]
  )

  const getLatestQueryParams = useCallback(
    () =>
      qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      }) as unknown as T,
    []
  )

  const updateQueryParams = (appliedQueryParams: T) => {
    navigate(
      {
        pathname: location.pathname,
        search: qs.stringify(
          {
            ...getLatestQueryParams(),
            ...appliedQueryParams,
          },
          { addQueryPrefix: false, ...qsStringifyOptions }
        ),
      },
      { replace: true }
    )
  }

  const setQueryParams = (appliedQueryParams: T) => {
    navigate(
      {
        pathname: location.pathname,
        search: qs.stringify(
          {
            ...appliedQueryParams,
          },
          { addQueryPrefix: false, ...qsStringifyOptions }
        ),
      },
      { replace: true }
    )
  }

  const removeQueryParams = (keys: (keyof T)[]) => {
    const freshParams = { ...getLatestQueryParams() }

    keys.forEach((key) => {
      delete freshParams[key]
    })

    navigate(
      {
        pathname: location.pathname,
        search: qs.stringify(freshParams, { addQueryPrefix: false }),
      },
      { replace: true }
    )
  }

  return {
    queryParams,
    removeQueryParams,
    setQueryParams,
    updateQueryParams,
  }
}
